<template>
	<div class="RadioSelectItem" role="radiogroup" :aria-labelledby="`radio-group-${item.value}`">
		<SelectItem
			:key="`${item.value}`"
			:item="parentItem"
			:type="SelectListItemType.RADIO"
			:name="name"
			:highlightSelectedText="highlightSelectedText"
			@selectionChange="handleStateUpdate"
		>
			<template v-if="item.icon" #icon>
				<i
					:class="`Icon ${item.icon}`"
					aria-hidden="true"
				></i>
			</template>
		</SelectItem>

		<div
			v-if="hasSubItems && showSubItems"
			class="SubItems"
			role="radiogroup"
			:aria-label="`${item.text} options`"
		>
			<SelectItem
				v-if="hasSubItems"
				v-for="(subItem, index) in displayedSubItems"
				:key="`${subItem.value}-${index}`"
				:item="subItem"
				:isSubItem="true"
				:type="SelectListItemType.RADIO"
				:name="name"
				:highlightSelectedText="highlightSelectedText"
				class="SubItem"
				@selectionChange="handleSubItemStateUpdate(subItem, $event)"
			/>

			<button
				v-if="showMoreIndicator"
				type="button"
				class="ShowAll"
				:aria-expanded="showAll"
				:aria-label="showAll ? 'Show fewer options' : `Show all ${allSubItemsCount} options`"
				@click="showHideAll"
			>
				{{ showAllText }}
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch, type PropType } from 'vue';
import ISelectItem from '@app/interfaces/dtos/ISelectItem';
import SelectItem from '@components/SelectItem/SelectItem.vue';
import { SelectListItemType } from '@app/enums/SelectListItemType';
import { SelectListItemStateType } from '@app/enums/SelectListItemStateType';

const props = defineProps({
	item: {
		type: Object as PropType<ISelectItem>,
		required: true,
	},
	subItems: {
		type: Array as PropType<ISelectItem[]>,
		required: false,
		default: () => [],
	},
	name: {
		type: String,
		required: false,
		default: 'radio-group',
	},
	highlightSelectedText: {
		type: Boolean,
		required: false,
		default: false,
	}
});

const parentItem = ref<ISelectItem>({ ...props.item });
const childItems = ref<ISelectItem[]>(props.subItems ? props.subItems.map(item => ({ ...item })) : []);
const showAll = ref(false);

const emit = defineEmits<{
	'selectionChange': [state: string, item?: ISelectItem];
}>();

watch(() => props.item, (newItem) => {
	parentItem.value = { ...newItem };
}, { deep: true });

watch(() => props.subItems, (newSubItems) => {
	childItems.value = newSubItems ? newSubItems.map(item => ({ ...item })) : [];
}, { deep: true });

const handleStateUpdate = (state: string) => {
	parentItem.value.state = state as SelectListItemStateType;

	if (state === SelectListItemStateType.SELECTED) {
		childItems.value.forEach((subItem: ISelectItem) => {
			subItem.state = SelectListItemStateType.ENABLED;
		});
	}

	emit('selectionChange', state, parentItem.value);
};

const handleSubItemStateUpdate = (updatedSubItem: ISelectItem, state: string) => {
	const index = childItems.value.findIndex((item: ISelectItem) => item.value === updatedSubItem.value);
	if (index !== -1) {
		childItems.value[index].state = state as SelectListItemStateType;
	}

	if (state === SelectListItemStateType.SELECTED) {
		parentItem.value.state = SelectListItemStateType.ENABLED;

		childItems.value.forEach((subItem: ISelectItem) => {
			if (subItem.value !== updatedSubItem.value) {
				subItem.state = SelectListItemStateType.ENABLED;
			}
		});
	}

	emit('selectionChange', state, updatedSubItem);
};

const hasSubItems = computed(() => {
	return childItems.value && childItems.value.length > 0;
});

const showSubItems = computed(() => {
	return parentItem.value.state === SelectListItemStateType.SELECTED
		|| childItems.value.some((subItem: ISelectItem) => subItem.state === SelectListItemStateType.SELECTED);
});

const showLimit = 7;

const allSubItemsCount = computed(() => {
	return childItems.value ? childItems.value.length : 0;
});

const showMoreIndicator = computed(() => {
	return allSubItemsCount.value > showLimit;
});

const displayedSubItems = computed(() => {
	if (showAll.value) return childItems.value;

	const baseItems = childItems.value.slice(0, showLimit);
	const selectedItem = childItems.value.find(
		(item: ISelectItem) => item.state === SelectListItemStateType.SELECTED
	);
	if (!selectedItem) return baseItems;

	if (baseItems.some((item: ISelectItem) => item.value === selectedItem.value)) return baseItems;

	return childItems.value.filter((item: ISelectItem, index: number) =>
		index < showLimit || item.value === selectedItem.value
	);
});

const showAllText = computed(() => {
	return showAll.value ? `Show less` : `Show all (${allSubItemsCount.value})`;
});

const showHideAll = () => {
	showAll.value = !showAll.value;
};
</script>

<style scoped lang="scss">
.RadioSelectItem {
	display: flex;
	flex-direction: column;

	@include PaddingAreas(0, 0, 1.5, 0);

	@include Breakpoint(Large, ExtraLarge) {
		@include PaddingAreas(0, 0, 1, 0);
	}

	.SubItems {
		border-bottom: 1px solid $Grey-200;

		.SubItem {
			@include PaddingAreas(0, 0, 1.5, 0);

			@include Breakpoint(Large, ExtraLarge) {
				@include PaddingAreas(0, 0, 1, 0);
			}
		}

		@include PaddingAreas(1, 0, 1, 3);
	}

	.ShowAll {
		cursor: pointer;
		color: $Blue-700;
		background: none;
		border: none;
		padding: 0;

		@include TextStyle(BodyMediumRegular);

		&:focus-visible {
			outline: none;
		}
	}
}
</style>
